import {showToast, ToastrHelper} from '$lib/helpers/toastrHelper';
import {
    ColumnNames,
    type GetAliiFeatureByNameResponse,
    type PutAliiFeatureRequest
} from '$lib/models/alii-features/types';
import {type BaseListResponse, DateFilters, type ErrorResponse, SortBy} from '$lib/models/types';
import {fetchWithAuth} from '$lib/utils/http/httpUtil';
import {extractErrorMessage, handleApiError} from '$lib/utils/error/errorUtils';
import type {State} from '@vincjo/datatables/remote';

function createFeatureFlagStore() {
  const type: string = 'Feature Flag';
  const baseUrl: string = '/api/alii-feature';

  return {
    fetchByName: async (name: string): Promise<GetAliiFeatureByNameResponse | ErrorResponse> => {
      const response = await fetchWithAuth(`${baseUrl}/${name}`, {
        method: 'GET',
      });

      if (response.status == 204) {
        return {
          message: ToastrHelper.notFound(type),
          detail: ToastrHelper.notFound(type), // Add detail field for Problem Details compatibility
        };
      }

      if (!response.ok) {
        const errorMessage = await extractErrorMessage(response, ToastrHelper.errorRetrieving(type));
        showToast({
          title: 'Error',
          message: errorMessage,
          type: 'error'
        });
        return {
          message: errorMessage,
          detail: errorMessage, // Add detail field for Problem Details compatibility
        };
      }

      try {
        return await response.json();
      } catch (error) {
        const errorMessage = handleApiError(error as Error, 'FeatureFlagStore.fetchByName');
        showToast({
          title: 'Error',
          message: errorMessage,
          type: 'error'
        });
        return {
          message: errorMessage,
          detail: errorMessage, // Add detail field for Problem Details compatibility
        };
      }
    },
    fetch: async (state: State): Promise<BaseListResponse> => {
      const { pageNumber, rowsPerPage, sort, filters } = state;

      let url = baseUrl;

      const sortBy: string =
        sort?.direction?.toString() != undefined ? (sort?.direction?.toString().toLowerCase() == 'asc' ? SortBy.Ascending : SortBy.Descending) : SortBy.Descending;

      url += `?pageNumber=${pageNumber}&pageSize=${rowsPerPage}&sortBy=${sortBy}&orderBy=${sort?.orderBy}`;
      if (filters) {
        url += filters
          .map(({ filterBy, value }) => {
            if (filterBy == ColumnNames.CreateDate) {
              const date = new Date();

              if (value === DateFilters.LastSevenDays) {
                date.setDate(date.getDate() - 7);
                return `&${filterBy}=${date.toISOString()}`;
              }
              if (value === DateFilters.LastFourteenDays) {
                date.setDate(date.getDate() - 14);
                return `&${filterBy}=${date.toISOString()}`;
              }
              if (value === DateFilters.LastMonth) {
                date.setMonth(date.getMonth() - 1);
                return `&${filterBy}=${date.toISOString()}`;
              }

              if (value == DateFilters.LastQuarter) {
                date.setMonth(date.getMonth() - 3);
                return `&${filterBy}=${date.toISOString()}`;
              }

              if (value == DateFilters.LastTwelveMonths) {
                date.setFullYear(date.getFullYear() - 1);
                return `&${filterBy}=${date.toISOString()}`;
              }

              return `&${filterBy}=${date.toISOString()}`;
            }

            return `&${filterBy}=${value}`;
          })
          .join('');
      }

      const response = await fetchWithAuth(url, {
        method: 'GET',
      });

      if (!response.ok) {
        console.error('Failed to fetch feature flags:', response.status, response.statusText);
        showToast({
          title: 'Error',
          message: `Failed to fetch feature flags (HTTP ${response.status})`,
          type: 'error'
        });
        return { total: 0, data: [] };
      }

      try {
        return await response.json();
      } catch (error) {
        console.error('Error parsing feature flags response:', error);
        showToast({
          title: 'Error',
          message: 'Failed to parse feature flags data',
          type: 'error'
        });
        return { total: 0, data: [] };
      }
    },
    fetchClients: async (
      featureName: string,
      search: string,
      excludedIds: number[],
      pageSize: number = 10
    ): Promise<Array<{ id: number; name: string }>> => {
      let url = `${baseUrl}/${featureName}/client?search=${encodeURIComponent(search)}&pageSize=${pageSize}`; // Change to name
      if (excludedIds.length > 0) {
        url += excludedIds.map((id) => `&additional=${id}`).join('');
      }
      const response = await fetchWithAuth(url, { method: 'GET' });
      if (!response.ok) return [];
      return await response.json();
    },
    update: async (request: PutAliiFeatureRequest): Promise<void> => {
      const response = await fetchWithAuth(`${baseUrl}/${request.name}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        const errorMessage = await extractErrorMessage(response, `Failed to update ${type} (HTTP ${response.status})`);
        showToast({
          title: 'Error',
          message: errorMessage,
          type: 'error'
        });
        throw new Error(errorMessage);
      }
    },
    fetchForClient: async(clientId: number, state: State, showClientOnly: boolean): Promise<BaseListResponse> => {
      const { pageNumber, rowsPerPage, sort, filters } = state;

      let url = `/api/client/${clientId}/featureflag`;

      const sortBy: string =
        sort?.direction?.toString() != undefined ? (sort?.direction?.toString().toLowerCase() === 'asc' ? SortBy.Ascending : SortBy.Descending) : SortBy.Descending;

      url += `?pageNumber=${pageNumber}&pageSize=${rowsPerPage}&sortBy=${sortBy}&orderBy=${sort?.orderBy}`;

        url += `&showClientOnly=${showClientOnly}`;

      if (filters) {
        url += filters
          .map(({ filterBy, value }) => {
            return `&${filterBy}=${value}`;
          })
          .join('');
      }

      const response = await fetchWithAuth(url, {
        method: 'GET',
      });

      if (!response.ok) {
        const errorMessage = await extractErrorMessage(response, `Failed to fetch users (HTTP ${response.status})`);
        showToast({
          title: 'Error',
          message: errorMessage,
          type: 'error'
        });
        return { total: 0, data: [] };
      }

      try {
        return await response.json();
      } catch (error) {
        showToast({
          title: 'Error',
          message: 'Failed to parse user data',
          type: 'error'
        });
        return { total: 0, data: [] };
      }
    },
  };
}

export const featureFlagStore = createFeatureFlagStore();

